import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import ChatWidget from './ChatWidget';
import { AgentSettings, QuickAction } from './AgentSettings';
import './taskpane.css';
import { getDocumentContext } from '../utils/office-helpers';

interface DevanaConfig {
  token: string;
  apiUrl: string;
  agentId: string;
  userIdentity?: string;
  hostcli?: string;
}

const STORAGE_KEYS = {
  TOKEN: 'devana_token',
  API_URL: 'devana_api_url',
  AGENT_ID: 'devana_agent_id',
  USER_IDENTITY: 'devana_user_identity',
  HOSTCLI: 'devana_hostcli',
  INSTANCE_TYPE: 'devana_instance_type',
  QUICK_ACTIONS: 'devana_quick_actions',
  SHOW_DEBUG: 'devana_show_debug',
  DOCUMENT_NAME: 'devana_document_name',
};

// Helper functions pour gérer le storage avec fallback vers localStorage
const StorageHelper = {
  get: (key: string): string | null => {
    if (Office?.context?.roamingSettings) {
      return Office.context.roamingSettings.get(key);
    }
    return localStorage.getItem(key);
  },

  set: (key: string, value: string): void => {
    if (Office?.context?.roamingSettings) {
      Office.context.roamingSettings.set(key, value);
    } else {
      localStorage.setItem(key, value);
    }
  },

  remove: (key: string): void => {
    if (Office?.context?.roamingSettings) {
      Office.context.roamingSettings.remove(key);
    } else {
      localStorage.removeItem(key);
    }
  },

  saveAsync: (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (Office?.context?.roamingSettings) {
        Office.context.roamingSettings.saveAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error('Failed to save settings'));
          }
        });
      } else {
        // localStorage sauvegarde automatiquement
        resolve();
      }
    });
  }
};

// Helper pour les logs conditionnels
const debugLog = {
  log: (...args: unknown[]) => {
    const showDebug = StorageHelper.get(STORAGE_KEYS.SHOW_DEBUG) === 'true';
    if (showDebug) {
      console.log(...args);
    }
  },
  error: (...args: unknown[]) => {
    console.error(...args);
  },
  warn: (...args: unknown[]) => {
    const showDebug = StorageHelper.get(STORAGE_KEYS.SHOW_DEBUG) === 'true';
    if (showDebug) {
      console.warn(...args);
    }
  }
};

// Écran de configuration
const ConfigScreen: React.FC<{ onConfigured: (config: DevanaConfig) => void; showDebug: boolean }> = ({ onConfigured, showDebug }) => {
  const [instanceType, setInstanceType] = useState<'production' | 'custom'>('production');
  const [apiUrl, setApiUrl] = useState('https://api.devana.ai');
  const [frontUrl, setFrontUrl] = useState('https://app.devana.ai');
  const [hostcli, setHostcli] = useState('app.devana.ai');
  const [status, setStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [officeReady, setOfficeReady] = useState(false);

  useEffect(() => {
    // Attendre que Office soit prêt
    let attempts = 0;
    const maxAttempts = 50; // 5 secondes max

    const checkOfficeReady = () => {
      attempts++;

      if (Office?.context?.roamingSettings) {
        setOfficeReady(true);
      } else if (attempts >= maxAttempts) {
        // Timeout - utiliser localStorage comme fallback
        debugLog.warn('Office.context.roamingSettings not available after timeout, using localStorage');
        setOfficeReady(true);
      } else {
        // Réessayer après un court délai
        setTimeout(checkOfficeReady, 100);
      }
    };

    checkOfficeReady();
  }, []);

  useEffect(() => {
    if (!officeReady) return;

    // Charger la configuration depuis le storage (persiste entre les sessions)
    try {
      const savedInstanceType = StorageHelper.get(STORAGE_KEYS.INSTANCE_TYPE);
      const savedApiUrl = StorageHelper.get(STORAGE_KEYS.API_URL);
      const savedHostcli = StorageHelper.get(STORAGE_KEYS.HOSTCLI);

      if (savedInstanceType && (savedInstanceType === 'production' || savedInstanceType === 'custom')) {
        setInstanceType(savedInstanceType);
      }
      if (savedApiUrl) setApiUrl(savedApiUrl);
      if (savedHostcli) setHostcli(savedHostcli);
    } catch (error) {
      debugLog.error('Error loading config:', error);
    }
  }, [officeReady]);

  const saveConfig = async () => {
    try {
      const finalApiUrl = instanceType === 'production' ? 'https://api.devana.ai' : apiUrl;
      const finalHostcli = instanceType === 'production' ? 'app.devana.ai' : hostcli;

      // Sauvegarder dans le storage (persiste entre les sessions)
      StorageHelper.set(STORAGE_KEYS.INSTANCE_TYPE, instanceType);
      StorageHelper.set(STORAGE_KEYS.API_URL, finalApiUrl);
      StorageHelper.set(STORAGE_KEYS.HOSTCLI, finalHostcli);

      await StorageHelper.saveAsync();

      setStatus({ type: 'success', message: 'Configuration sauvegardée avec succès!' });
      setTimeout(() => {
        setStatus(null);
      }, 3000);
    } catch (error) {
      debugLog.error('Error saving config:', error);
      setStatus({ type: 'error', message: 'Erreur lors de la sauvegarde' });
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);

    if (!email || !password) {
      setStatus({ type: 'error', message: 'Veuillez remplir tous les champs' });
      return;
    }

    setIsLoading(true);

    try {
      const savedInstanceType = StorageHelper.get(STORAGE_KEYS.INSTANCE_TYPE) || instanceType;
      const finalApiUrl = StorageHelper.get(STORAGE_KEYS.API_URL) || 'https://api.devana.ai';
      const finalHostcli = StorageHelper.get(STORAGE_KEYS.HOSTCLI) || 'app.devana.ai';

      debugLog.log('[Login] Tentative de connexion...');
      debugLog.log('[Login] API URL:', finalApiUrl);
      debugLog.log('[Login] Hostcli:', finalHostcli);
      debugLog.log('[Login] Email:', email.trim().toLowerCase());

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (finalHostcli && finalHostcli.trim().length > 0) {
        headers['hostcli'] = finalHostcli;
      }

      debugLog.log('[Login] Headers:', headers);

      const response = await fetch(`${finalApiUrl}/graphql`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: `
            mutation Login($email: String!, $password: String!) {
              login(email: $email, password: $password)
            }
          `,
          variables: {
            email: email.trim().toLowerCase(),
            password,
          },
        }),
      });

      debugLog.log('[Login] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        debugLog.error('[Login] HTTP Error response:', errorText);
        throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}${errorText ? ' - ' + errorText : ''}`);
      }

      const data = await response.json();
      debugLog.log('[Login] Response data:', data);

      if (data.errors) {
        debugLog.error('[Login] GraphQL errors:', data.errors);
        throw new Error(data.errors[0].message || 'Erreur de connexion');
      }

      const token = data.data?.login;

      if (!token) {
        debugLog.error('[Login] Token missing in response');
        throw new Error('Token non reçu du serveur');
      }

      debugLog.log('[Login] ✅ Login successful, token received');

      // Sauvegarder le token et la config dans le storage (persiste entre les sessions)
      StorageHelper.set(STORAGE_KEYS.TOKEN, token);
      StorageHelper.set(STORAGE_KEYS.AGENT_ID, 'devana');
      StorageHelper.set(STORAGE_KEYS.API_URL, finalApiUrl);
      StorageHelper.set(STORAGE_KEYS.HOSTCLI, finalHostcli);
      StorageHelper.set(STORAGE_KEYS.INSTANCE_TYPE, savedInstanceType);

      await StorageHelper.saveAsync();

      setStatus({ type: 'success', message: 'Connexion réussie!' });

      setTimeout(() => {
        onConfigured({
          token,
          apiUrl: finalApiUrl,
          agentId: 'devana',
          hostcli: finalHostcli,
        });
      }, 1000);
    } catch (err) {
      debugLog.error('[Login] ❌ Error:', err);

      let errorMessage = (err as Error).message;
      let detailedError = '';

      // Ajouter des détails techniques pour debug uniquement si le mode debug est activé
      if (err instanceof Error && showDebug) {
        detailedError = `\n\nDétails techniques:\n`;
        detailedError += `- Message: ${err.message}\n`;
        detailedError += `- Type: ${err.name}\n`;
        if (err.stack) {
          detailedError += `- Stack (first line): ${err.stack.split('\n')[0]}\n`;
        }
      }

      // Messages d'erreur conviviaux
      if (errorMessage.includes('User not found')) {
        errorMessage = '❌ Utilisateur non trouvé. Vérifiez votre email.' + detailedError;
      } else if (errorMessage.includes('Invalid password')) {
        errorMessage = '❌ Mot de passe incorrect.' + detailedError;
      } else if (errorMessage.includes('Network') || errorMessage.includes('Failed to fetch') || errorMessage.includes('Load failed')) {
        const apiUrl = StorageHelper.get(STORAGE_KEYS.API_URL) || 'https://api.devana.ai';
        errorMessage = '❌ Erreur de connexion réseau.\n\n';

        if (showDebug) {
          errorMessage += 'Vérifications:\n';
          errorMessage += `- API URL: ${apiUrl}\n`;

          // Détecter le problème HTTP/HTTPS
          if (apiUrl.startsWith('http://') && window.location.protocol === 'https:') {
            errorMessage += '\n⚠️ PROBLÈME DÉTECTÉ: Mixed Content!\n';
            errorMessage += 'Vous essayez d\'appeler une API HTTP depuis une page HTTPS.\n';
            errorMessage += 'Solutions:\n';
            errorMessage += '  1. Utilisez HTTPS pour votre API locale\n';
            errorMessage += '  2. OU changez l\'URL API en: http://localhost:4666\n';
            errorMessage += '     et rechargez l\'add-in depuis http://localhost:3000\n';
          }

          errorMessage += `- Internet: Vérifiez votre connexion\n`;
          errorMessage += `- Firewall: Vérifiez qu'il n'est pas bloqué\n`;
          errorMessage += `- CORS: Vérifiez que le serveur autorise ${window.location.origin}\n`;
          errorMessage += detailedError;
        } else {
          errorMessage += 'Vérifiez votre connexion et l\'URL de l\'API.\nActivez le mode debug dans les paramètres pour plus de détails.';
        }
      } else if (errorMessage.includes('HTTP')) {
        errorMessage = `❌ Erreur serveur: ${errorMessage}\n`;
        if (showDebug) {
          errorMessage += `\nVérifications:\n`;
          errorMessage += `- L'API est-elle accessible?\n`;
          errorMessage += `- URL correcte: ${StorageHelper.get(STORAGE_KEYS.API_URL)}\n`;
          errorMessage += detailedError;
        }
      } else {
        errorMessage = `❌ ${errorMessage}${detailedError}`;
      }

      debugLog.error('[Login] Full error details:', errorMessage);
      setStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      padding: '20px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    }}>
      <h1 style={{
        fontSize: '20px',
        color: '#7C5DFA',
        textAlign: 'center',
      }}>
        Configuration Devana
      </h1>

      {/* Diagnostic Panel */}
      {showDebug && (
        <details style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.7)' }}>
          <summary style={{ cursor: 'pointer', userSelect: 'none', padding: '8px', background: 'rgba(124, 93, 250, 0.1)', borderRadius: '6px' }}>
            🔍 Diagnostic Connexion
          </summary>
        <div style={{
          marginTop: '8px',
          padding: '12px',
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '6px',
          fontFamily: 'monospace',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}>
          <div><strong>Protocole actuel:</strong> {window.location.protocol}</div>
          <div><strong>Host actuel:</strong> {window.location.host}</div>
          <div><strong>Origin actuel:</strong> {window.location.origin}</div>
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '4px 0' }} />
          <div><strong>API URL configurée:</strong> {StorageHelper.get(STORAGE_KEYS.API_URL) || 'Non configuré'}</div>
          <div><strong>Hostcli configuré:</strong> {StorageHelper.get(STORAGE_KEYS.HOSTCLI) || 'Non configuré'}</div>
          <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '4px 0' }} />
          {StorageHelper.get(STORAGE_KEYS.API_URL)?.startsWith('http://') && window.location.protocol === 'https:' && (
            <div style={{ color: '#FF9292', background: 'rgba(255, 146, 146, 0.15)', padding: '8px', borderRadius: '4px' }}>
              ⚠️ <strong>ATTENTION:</strong> Problème Mixed Content détecté!<br/>
              HTTPS ne peut pas appeler HTTP.
            </div>
          )}
          <div style={{ marginTop: '8px', fontSize: '10px', opacity: 0.7 }}>
            Astuce: En dev, utilisez http:// pour les deux ou https:// pour les deux
          </div>
        </div>
        </details>
      )}

      <div>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: 600,
        }}>
          Instance Devana
        </label>
        <select
          value={instanceType}
          onChange={(e) => setInstanceType(e.target.value as 'production' | 'custom')}
          style={{
            width: '100%',
            padding: '10px 12px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(124, 93, 250, 0.3)',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
          }}
        >
          <option value="production">Production (app.devana.ai)</option>
          <option value="custom">Personnalisée</option>
        </select>
      </div>

      {instanceType === 'custom' && (
        <>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 600,
            }}>
              URL de l'API
            </label>
            <input
              type="text"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              placeholder="https://api.devana.ai"
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(124, 93, 250, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 600,
            }}>
              URL du Frontend
            </label>
            <input
              type="text"
              value={frontUrl}
              onChange={(e) => setFrontUrl(e.target.value)}
              placeholder="https://app.devana.ai"
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(124, 93, 250, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 600,
            }}>
              Hostcli
            </label>
            <input
              type="text"
              value={hostcli}
              onChange={(e) => setHostcli(e.target.value)}
              placeholder="app.devana.ai"
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(124, 93, 250, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
              }}
            />
            <div style={{
              marginTop: '6px',
              fontSize: '11px',
              color: 'rgba(255, 255, 255, 0.5)',
            }}>
              URL du frontend sans http:// ou https://
            </div>
          </div>
        </>
      )}

      <button
        onClick={saveConfig}
        disabled={!officeReady}
        style={{
          width: '100%',
          padding: '12px',
          background: !officeReady
            ? 'rgba(124, 93, 250, 0.3)'
            : 'linear-gradient(135deg, #7C5DFA 0%, #6A4DE8 100%)',
          border: 'none',
          borderRadius: '8px',
          color: 'white',
          fontSize: '14px',
          fontWeight: 600,
          cursor: !officeReady ? 'not-allowed' : 'pointer',
        }}
      >
        {!officeReady ? 'Chargement...' : 'Sauvegarder la configuration'}
      </button>

      <div style={{ height: '1px', background: 'rgba(124, 93, 250, 0.2)' }} />

      {!showLoginForm ? (
        <button
          onClick={() => {
            saveConfig();
            setTimeout(() => setShowLoginForm(true), 500);
          }}
          disabled={!officeReady}
          style={{
            width: '100%',
            padding: '12px',
            background: !officeReady
              ? 'rgba(72, 199, 116, 0.3)'
              : 'linear-gradient(135deg, #48C774 0%, #3AA65D 100%)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: !officeReady ? 'not-allowed' : 'pointer',
          }}
        >
          {!officeReady ? 'Chargement...' : 'Se connecter'}
        </button>
      ) : (
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 600,
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="hello@devana.ai"
              required
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(124, 93, 250, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 600,
            }}>
              Mot de passe
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '10px 40px 10px 12px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(124, 93, 250, 0.3)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                  padding: '4px',
                  fontSize: '16px',
                }}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={!officeReady || isLoading || !email || !password}
            style={{
              width: '100%',
              padding: '12px',
              background: (!officeReady || isLoading || !email || !password)
                ? 'rgba(124, 93, 250, 0.3)'
                : 'linear-gradient(135deg, #48C774 0%, #3AA65D 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: (!officeReady || isLoading || !email || !password) ? 'not-allowed' : 'pointer',
            }}
          >
            {!officeReady ? 'Chargement...' : (isLoading ? 'Connexion...' : 'Se connecter')}
          </button>
        </form>
      )}

      {status && (
        <div style={{
          padding: '12px',
          borderRadius: '8px',
          fontSize: '12px',
          textAlign: 'left',
          background: status.type === 'success'
            ? 'rgba(72, 199, 116, 0.15)'
            : 'rgba(255, 146, 146, 0.15)',
          border: status.type === 'success'
            ? '1px solid rgba(72, 199, 116, 0.3)'
            : '1px solid rgba(255, 146, 146, 0.3)',
          color: status.type === 'success' ? '#48C774' : '#FF9292',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          maxHeight: '300px',
          overflowY: 'auto',
        }}>
          {status.message}
        </div>
      )}
    </div>
  );
};

const App: React.FC = () => {
  const [config, setConfig] = useState<DevanaConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [officeReady, setOfficeReady] = useState(false);
  const [quickActions, setQuickActions] = useState<QuickAction[]>([]);
  const [showDebug, setShowDebug] = useState(false);
  const [documentName, setDocumentName] = useState('');

  useEffect(() => {
    // Attendre que Office soit prêt
    let attempts = 0;
    const maxAttempts = 50; // 5 secondes max

    const checkOfficeReady = () => {
      attempts++;

      if (Office?.context?.roamingSettings) {
        setOfficeReady(true);
      } else if (attempts >= maxAttempts) {
        // Timeout - utiliser localStorage comme fallback
        debugLog.warn('Office.context.roamingSettings not available after timeout, using localStorage');
        setOfficeReady(true);
      } else {
        // Réessayer après un court délai
        setTimeout(checkOfficeReady, 100);
      }
    };

    checkOfficeReady();
  }, []);

  useEffect(() => {
    if (!officeReady) return;

    // Charger la config depuis le storage (persiste entre les sessions)
    try {
      const token = StorageHelper.get(STORAGE_KEYS.TOKEN);
      const apiUrl = StorageHelper.get(STORAGE_KEYS.API_URL) || 'https://api.devana.ai';
      const agentId = StorageHelper.get(STORAGE_KEYS.AGENT_ID) || 'devana';
      const userIdentity = StorageHelper.get(STORAGE_KEYS.USER_IDENTITY) || undefined;
      const hostcli = StorageHelper.get(STORAGE_KEYS.HOSTCLI) || 'app.devana.ai';

      // Charger les actions rapides personnalisées
      const savedActionsStr = StorageHelper.get(STORAGE_KEYS.QUICK_ACTIONS);
      if (savedActionsStr) {
        try {
          const savedActions = JSON.parse(savedActionsStr) as QuickAction[];
          setQuickActions(savedActions);
        } catch (e) {
          debugLog.error('Error parsing quick actions:', e);
        }
      }

      // Charger l'option show debug
      const savedShowDebug = StorageHelper.get(STORAGE_KEYS.SHOW_DEBUG);
      setShowDebug(savedShowDebug === 'true');

      // Charger le nom du document personnalisé
      const savedDocName = StorageHelper.get(STORAGE_KEYS.DOCUMENT_NAME);
      if (savedDocName) {
        setDocumentName(savedDocName);
      }

      if (token) {
        setConfig({
          token,
          apiUrl,
          agentId,
          userIdentity,
          hostcli,
        });
      }

      setIsLoading(false);
    } catch (error) {
      debugLog.error('Error loading config:', error);
      setIsLoading(false);
    }
  }, [officeReady]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        background: 'linear-gradient(135deg, #1a1d29 0%, #2a1d3f 50%, #1a1d29 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Effet de brillance animé en arrière-plan */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle at center, rgba(124, 93, 250, 0.1) 0%, transparent 70%)',
          animation: 'pulse 3s ease-in-out infinite',
        }} />

        {/* Spinner personnalisé */}
        <div style={{
          width: '60px',
          height: '60px',
          border: '3px solid rgba(124, 93, 250, 0.2)',
          borderTop: '3px solid #7C5DFA',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '24px',
          boxShadow: '0 0 20px rgba(124, 93, 250, 0.3)',
        }} />

        {/* Texte de chargement */}
        <div style={{
          fontSize: '18px',
          fontWeight: 600,
          color: '#fff',
          marginBottom: '8px',
          animation: 'fadeInOut 2s ease-in-out infinite',
        }}>
          Connexion à Devana
        </div>

        <div style={{
          fontSize: '13px',
          color: 'rgba(255, 255, 255, 0.6)',
          animation: 'fadeInOut 2s ease-in-out infinite 0.5s',
        }}>
          Chargement de votre session...
        </div>

        {/* Barre de progression stylée */}
        <div style={{
          width: '200px',
          height: '3px',
          background: 'rgba(124, 93, 250, 0.2)',
          borderRadius: '2px',
          marginTop: '24px',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: '60%',
            background: 'linear-gradient(90deg, transparent, #7C5DFA, transparent)',
            animation: 'slide 1.5s ease-in-out infinite',
            boxShadow: '0 0 10px #7C5DFA',
          }} />
        </div>

        {/* Styles d'animation inline */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }

            @keyframes fadeInOut {
              0%, 100% { opacity: 0.6; }
              50% { opacity: 1; }
            }

            @keyframes pulse {
              0%, 100% { opacity: 0.5; transform: scale(1); }
              50% { opacity: 0.8; transform: scale(1.05); }
            }

            @keyframes slide {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(500%); }
            }
          `
        }} />
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      // Supprimer le token du storage
      StorageHelper.remove(STORAGE_KEYS.TOKEN);
      StorageHelper.remove(STORAGE_KEYS.AGENT_ID);
      StorageHelper.remove(STORAGE_KEYS.USER_IDENTITY);

      await StorageHelper.saveAsync();

      // Réinitialiser la config pour revenir à l'écran de login
      setConfig(null);
    } catch (error) {
      debugLog.error('Error during logout:', error);
    }
  };

  const handleAgentChange = async (newAgentId: string) => {
    try {
      // Sauvegarder le nouvel agent dans le storage
      StorageHelper.set(STORAGE_KEYS.AGENT_ID, newAgentId);
      await StorageHelper.saveAsync();

      // Mettre à jour la config
      setConfig(prev => prev ? { ...prev, agentId: newAgentId } : null);
      setShowSettings(false);
    } catch (error) {
      debugLog.error('Error changing agent:', error);
    }
  };

  const handleIdentityChange = async (newIdentity: string) => {
    try {
      // Sauvegarder la nouvelle identité dans le storage
      StorageHelper.set(STORAGE_KEYS.USER_IDENTITY, newIdentity);
      await StorageHelper.saveAsync();

      // Mettre à jour la config
      setConfig(prev => prev ? { ...prev, userIdentity: newIdentity } : null);
    } catch (error) {
      debugLog.error('Error changing identity:', error);
    }
  };

  const handleQuickActionsChange = async (actions: QuickAction[]) => {
    try {
      // Sauvegarder les actions rapides dans le storage
      StorageHelper.set(STORAGE_KEYS.QUICK_ACTIONS, JSON.stringify(actions));
      await StorageHelper.saveAsync();

      // Mettre à jour l'état
      setQuickActions(actions);
    } catch (error) {
      debugLog.error('Error saving quick actions:', error);
    }
  };

  const handleDebugToggle = async (show: boolean) => {
    try {
      // Sauvegarder l'option dans le storage
      StorageHelper.set(STORAGE_KEYS.SHOW_DEBUG, show.toString());
      await StorageHelper.saveAsync();

      // Mettre à jour l'état
      setShowDebug(show);
      setShowSettings(false);
    } catch (error) {
      debugLog.error('Error saving debug setting:', error);
    }
  };

  const handleDocumentNameChange = async (name: string) => {
    try {
      // Sauvegarder le nom dans le storage
      StorageHelper.set(STORAGE_KEYS.DOCUMENT_NAME, name);
      await StorageHelper.saveAsync();

      // Mettre à jour l'état
      setDocumentName(name);
      setShowSettings(false);
    } catch (error) {
      debugLog.error('Error saving document name:', error);
    }
  };

  if (!config) {
    return <ConfigScreen onConfigured={setConfig} showDebug={showDebug} />;
  }

  return (
    <>
      <ChatWidget
        token={config.token}
        apiUrl={config.apiUrl}
        agentId={config.agentId}
        userIdentity={config.userIdentity}
        hostcli={config.hostcli}
        customQuickActions={quickActions}
        showDebug={showDebug}
        documentName={documentName}
        onLogout={handleLogout}
        onOpenSettings={() => setShowSettings(true)}
      />
      {showSettings && (
        <AgentSettings
          apiUrl={config.apiUrl}
          token={config.token}
          currentAgentId={config.agentId}
          userIdentity={config.userIdentity || ''}
          hostcli={config.hostcli}
          quickActions={quickActions}
          showDebug={showDebug}
          documentName={documentName}
          onAgentChange={handleAgentChange}
          onIdentityChange={handleIdentityChange}
          onQuickActionsChange={handleQuickActionsChange}
          onDebugToggle={handleDebugToggle}
          onDocumentNameChange={handleDocumentNameChange}
          onClose={() => setShowSettings(false)}
        />
      )}
    </>
  );
};

Office.onReady(() => {
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );

  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
});
